import {
  type BombeoCompletion,
  bombeoCompletion,
  type BombeoMetrics,
  bombeoMetrics,
  type Muelle,
  type PlaceEvent,
  type ScaleEvent,
  sortEventsDesc,
} from '../domain/scales'
import { bombeoDurationWarning, type DurationWarning } from './bombeoDuration'
import { shownEvents, type StayWindow } from './sostratBombeo'
import { scaleTimeline } from './timeline'
import type { EventRow, MarpolAnnex, ScaleKey, ScaleRow, ScaleWithEventRows } from './types'

export const scaleRecordId = ({ year, scaleId }: ScaleKey): string => `${String(year)}-${scaleId}`

export const parseScaleRecordId = (id: string): ScaleKey | undefined => {
  const match = /^(\d{4})-(.+)$/.exec(id)
  return match ? { year: Number(match[1]), scaleId: match[2] ?? '' } : undefined
}

export const currentPlaceEvent = (events: readonly ScaleEvent[], now: number): PlaceEvent | undefined => {
  const started = sortEventsDesc(events.filter((e): e is PlaceEvent => e.type === 'PLACE' && Date.parse(e.startTime) <= now))
  return started.find(e => e.endTime === null || Date.parse(e.endTime) >= now) ?? started[0]
}

type StayEndFields = Pick<EventRow, 'endTime' | 'actualEndTime' | 'actualsState'>

const stayEnded = (stay: StayEndFields, now: number): boolean => {
  if (stay.actualEndTime !== null) return Date.parse(stay.actualEndTime) <= now
  return stay.actualsState === 'UNCONFIRMED' && stay.endTime !== null && Date.parse(stay.endTime) <= now
}

export const isClosed = (events: readonly EventRow[], now: number): boolean => {
  const stays = events.filter(e => e.type === 'PLACE')
  return stays.length > 0 && stays.every(e => stayEnded(e, now))
}

export type ScaleState = 'PLANNED' | 'IN_PROGRESS' | 'CLOSED' | 'FINISHED'

const scaleState = ({ scale, events }: ScaleWithEventRows, now: number): ScaleState => {
  if (scale.finished) return 'FINISHED'
  if (isClosed(events, now)) return 'CLOSED'
  return events.some(e => e.type === 'PLACE' && Date.parse(e.startTime) <= now) ? 'IN_PROGRESS' : 'PLANNED'
}

type BombeoStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'DONE'

const bombeoStatus = (events: readonly ScaleEvent[], now: number): BombeoStatus => {
  const bombeos = events.filter(e => e.type === 'BOMBEO')
  const ended = (e: ScaleEvent): boolean => e.endTime !== null && Date.parse(e.endTime) <= now
  if (bombeos.length > 0 && bombeos.every(ended)) return 'DONE'
  if (bombeos.some(e => Date.parse(e.startTime) <= now)) return 'IN_PROGRESS'
  return 'NOT_STARTED'
}

export type EventProgress = 'NOT_STARTED' | 'IN_PROGRESS' | 'DONE'

export const eventProgress = (event: EventRow, now: number): EventProgress => {
  if (event.type === 'PLACE' ? stayEnded(event, now) : event.endTime !== null) return 'DONE'
  return Date.parse(event.actualStartTime ?? event.startTime) > now ? 'NOT_STARTED' : 'IN_PROGRESS'
}

const sostratBombeoProgress = (event: ScaleEvent, now: number): EventProgress => {
  if (event.endTime !== null && Date.parse(event.endTime) <= now) return 'DONE'
  return Date.parse(event.startTime) > now ? 'NOT_STARTED' : 'IN_PROGRESS'
}

export interface EventMarker {
  id: string
  type: ScaleEvent['type']
  muelle: Muelle | null
  marpolAnnex: MarpolAnnex | null
  number: number | null
  product: string | null
  quantityT: number | null
  startTime: string
  endTime: string | null
  progress: EventProgress
  fromSostrat: boolean
  durationWarning: DurationWarning | null
  outsideStay: StayWindow | null
  endUnconfirmed: boolean
}

export const eventMarkers = (events: readonly EventRow[], now: number): EventMarker[] =>
  scaleTimeline(shownEvents(events), event => event.stayId).flatMap(column =>
    column.entries
      .filter(entry => entry.kind === 'ARRIVAL' || entry.kind === 'START')
      .map(({ event, number }) => ({
        id: event.id,
        type: event.type,
        muelle: event.type === 'PLACE' ? event.muelle : null,
        marpolAnnex: event.marpolAnnex,
        number,
        product: event.type === 'BOMBEO' ? event.product : null,
        quantityT: event.type === 'BOMBEO' ? event.quantityT : null,
        startTime: event.actualStartTime ?? event.startTime,
        endTime: event.actualEndTime ?? event.endTime,
        progress: event.fromSostrat ? sostratBombeoProgress(event, now) : eventProgress(event, now),
        fromSostrat: event.fromSostrat,
        durationWarning: bombeoDurationWarning(event, event.quantityT),
        outsideStay: event.outsideStay,
        endUnconfirmed: event.type === 'PLACE' && event.actualEndTime === null && event.actualsState === 'UNCONFIRMED',
      })),
  )

export interface ScaleRecord extends ScaleRow {
  id: string
  currentMuelle: Muelle | null
  currentStartTime: string | null
  currentEndTime: string | null
  bombeoStatus: BombeoStatus
  eventMarkers: EventMarker[]
  bombeoMetrics: BombeoMetrics
  bombeoCompletion: BombeoCompletion
  terminals: string[]
  state: ScaleState
  closed: boolean
}

export type EventRecord = EventRow & { scaleRecordId: string }

export const toEventRecord = (event: EventRow): EventRecord => ({
  ...event,
  scaleRecordId: scaleRecordId({ year: event.year, scaleId: event.scaleId }),
})

export const summarise = (found: ScaleWithEventRows, now: number): ScaleRecord => {
  const { scale, events } = found
  const current = currentPlaceEvent(events, now)
  const shown = shownEvents(events)
  const metrics = bombeoMetrics(shown)
  return {
    ...scale,
    id: scaleRecordId(scale),
    currentMuelle: current?.muelle ?? null,
    currentStartTime: current?.startTime ?? null,
    currentEndTime: current?.endTime ?? null,
    bombeoStatus: bombeoStatus(shown, now),
    eventMarkers: eventMarkers(events, now),
    bombeoMetrics: metrics,
    bombeoCompletion: bombeoCompletion(metrics),
    terminals: [
      ...new Set(
        sortEventsDesc(events)
          .reverse()
          .flatMap(event => (event.type === 'PLACE' ? event.terminals : [])),
      ),
    ],
    state: scaleState(found, now),
    closed: isClosed(events, now),
  }
}
